/**
 * Bulk Import Service
 *
 * Executes batch imports of validated CSV data using GraphQL mutations.
 * Uses Promise.allSettled for parallel execution with error handling.
 *
 * @module ImportExportTab/services/bulkImportService
 */

import { API } from 'aws-amplify';
import { createOrgUnit, createLocation } from 'graphql/mutations';
import { IMPORT_BATCH_SIZE } from '../constants';

/**
 * Import OrgUnits in batches
 *
 * Executes createOrgUnit mutations for each valid record.
 * Uses batching and Promise.allSettled for parallel execution.
 *
 * @param {Object[]} records - Valid OrgUnit records from CSV
 * @param {string} clientId - Client ID for multi-tenancy
 * @param {Object} user - Current user object
 * @param {Object[]} existingOrgUnits - Existing OrgUnits for parent lookup
 * @param {Object} [options] - Optional configuration
 * @param {number} [options.batchSize] - Batch size (default: 50)
 * @returns {Promise<Object>} Import results
 * @returns {Object[]} result.success - Successfully created records
 * @returns {Object[]} result.errors - Failed records with error messages
 * @returns {Object} result.summary - Summary statistics
 * @returns {Object} result.metadata - Import metadata
 *
 * @example
 * const result = await importOrgUnits(validRecords, clientId, user, existingOrgUnits);
 * console.log(`Success: ${result.summary.successCount}, Errors: ${result.summary.errorCount}`);
 */
export async function importOrgUnits(records, clientId, user, existingOrgUnits = [], options = {}) {
  // Build lookup map of OrgUnit names to IDs (for parent references)
  // Start with existing OrgUnits
  const orgUnitNameToId = new Map();
  existingOrgUnits.forEach(ou => {
    orgUnitNameToId.set(ou.name, ou.id);
  });

  // Group records by hierarchy level
  const levelOrder = ['ORGLEVEL_3', 'ORGLEVEL_2', 'ORGLEVEL_1'];
  const recordsByLevel = {
    'ORGLEVEL_3': [],
    'ORGLEVEL_2': [],
    'ORGLEVEL_1': []
  };

  records.forEach(record => {
    if (recordsByLevel[record.orgLevel]) {
      recordsByLevel[record.orgLevel].push(record);
    }
  });

  // Process each level in order, with parallel processing within each level
  const allResults = [];
  const processedRecords = [];

  for (const level of levelOrder) {
    const levelRecords = recordsByLevel[level];
    if (levelRecords.length === 0) continue;

    // Process all records at this level in parallel
    const levelPromises = levelRecords.map(async (record) => {
      // Resolve parent OrgUnit ID from name
      let parentOrgUnitId = null;
      if (record.parentOrgUnitName && record.parentOrgUnitName.trim()) {
        parentOrgUnitId = orgUnitNameToId.get(record.parentOrgUnitName) || null;
      }

      const input = {
        clientId,
        name: record.name,
        orgLevel: record.orgLevel,
        parentOrgUnitId,
        isActive: record.isActive === 'TRUE'
      };

      // Create OrgUnit
      return API.graphql({
        query: createOrgUnit,
        variables: { input }
      });
    });

    // Wait for all OrgUnits at this level to complete
    const levelResults = await Promise.allSettled(levelPromises);

    // Add newly created OrgUnits to lookup map before processing next level
    levelResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const newOrgUnit = result.value.data.createOrgUnit;
        if (newOrgUnit && newOrgUnit.id) {
          orgUnitNameToId.set(newOrgUnit.name, newOrgUnit.id);
        }
      }
    });

    // Collect results and records in order
    allResults.push(...levelResults);
    processedRecords.push(...levelRecords);
  }

  // Process results
  return processResults(allResults, processedRecords, {
    clientId,
    importType: 'orgUnits',
    timestamp: new Date().toISOString(),
    user: user.username
  });
}

/**
 * Import Locations in batches
 *
 * Executes createLocation mutations for each valid record.
 *
 * @param {Object[]} records - Valid Location records from CSV
 * @param {string} clientId - Client ID for multi-tenancy
 * @param {Object} user - Current user object
 * @param {Object[]} existingOrgUnits - Existing OrgUnits for orgLevel_1 lookup
 * @param {Object} [options] - Optional configuration
 * @returns {Promise<Object>} Import results
 *
 * @example
 * const result = await importLocations(validRecords, clientId, user, existingOrgUnits);
 */
export async function importLocations(records, clientId, user, existingOrgUnits = [], options = {}) {
  const batchSize = options.batchSize || IMPORT_BATCH_SIZE;

  // Build lookup map of OrgUnit names to IDs (for orgLevel_1 references)
  const orgUnitNameToId = new Map();
  existingOrgUnits.forEach(ou => {
    if (ou.orgLevel === 'ORGLEVEL_1') {
      orgUnitNameToId.set(ou.name, ou.id);
    }
  });

  // Create mutation function
  const createLocationMutation = async (record) => {
    // Resolve orgLevel_1 ID from name
    let orgLevel_1 = null;
    if (record.orgLevel_1_name && record.orgLevel_1_name.trim()) {
      orgLevel_1 = orgUnitNameToId.get(record.orgLevel_1_name) || null;
    }

    // Structure address object
    const address = {
      street_1: record.street_1 || '',
      street_2: record.street_2 || '',
      city: record.city || '',
      state: record.state || '',
      zipCode: record.zipCode || '',
      country: 'USA' // Default to USA
    };

    const input = {
      clientId,
      locationName: record.locationName,
      locationNumber: record.locationNumber || null,
      orgLevel_1,
      address,
      timeZone: record.timeZone,
      ownershipType: record.ownershipType || null,
      openDate: record.openDate || null,
      conceptId: record.conceptId || null
    };

    return API.graphql({
      query: createLocation,
      variables: { input }
    });
  };

  // Execute batched mutations
  const results = await batchExecute(records, createLocationMutation, batchSize);

  // Process results
  return processResults(results, records, {
    clientId,
    importType: 'locations',
    timestamp: new Date().toISOString(),
    user: user.username
  });
}

/**
 * Execute mutations in batches with Promise.allSettled
 *
 * Processes records in batches to avoid overwhelming the API.
 * Uses Promise.allSettled to handle partial failures gracefully.
 *
 * @param {Object[]} records - Records to process
 * @param {Function} mutationFn - Async function that executes mutation for one record
 * @param {number} batchSize - Number of records per batch
 * @returns {Promise<Object[]>} Array of Promise.allSettled results
 *
 * @example
 * const results = await batchExecute(records, createMutation, 50);
 */
export async function batchExecute(records, mutationFn, batchSize) {
  if (!records || records.length === 0) {
    return [];
  }

  const allResults = [];

  // Process in batches
  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);

    // Execute all mutations in this batch in parallel
    const batchPromises = batch.map(record => mutationFn(record));
    const batchResults = await Promise.allSettled(batchPromises);

    allResults.push(...batchResults);
  }

  return allResults;
}

/**
 * Process Promise.allSettled results into structured output
 *
 * @private
 * @param {Object[]} results - Promise.allSettled results
 * @param {Object[]} originalRecords - Original records array
 * @param {Object} metadata - Metadata to include in results
 * @returns {Object} Structured results object
 */
function processResults(results, originalRecords, metadata) {
  const success = [];
  const errors = [];

  results.forEach((result, index) => {
    const originalRecord = originalRecords[index];

    if (result.status === 'fulfilled') {
      success.push({
        record: originalRecord,
        result: result.value
      });
    } else {
      errors.push({
        record: originalRecord,
        error: result.reason?.message || result.reason?.errors?.[0]?.message || String(result.reason)
      });
    }
  });

  return {
    success,
    errors,
    summary: {
      totalRecords: originalRecords.length,
      successCount: success.length,
      errorCount: errors.length
    },
    metadata
  };
}
